import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../../../App/configs/Store";
import {
  dayOfficeMarketCountThunk,
  getDayOfficeListThunk,
} from "../../thunks/BookThunks";
import {
  dayOfficeFilterDataSelector,
  modalConfirmBookingSelector,
} from "../../selectors/BookSelectors";
import SizeBox from "../../../../components/SizeBox";
import DayOfficeList from "./DayOfficeList";
import { listDayOfficeFilterAction } from "../../actions/BookActions";
import FilterDayOffice from "../Components/FilterDayOffice";
import ModalConfirmBookingDayOffice from "../Components/ModalConfirmBookingDayOffice";
import ModalBookingSubmittedDayOffice from "../Components/ModalBookingSubmittedDayOffice";

const DayOffice = () => {
  const dispatch = useDispatch<AppDispatch>();
  const dayOfficeFilterData = useSelector(dayOfficeFilterDataSelector);
  const modalConfirmBooking = useSelector(modalConfirmBookingSelector);
  const [populateFromSessionStorage, setPopulateFromSessionStorage] =
    useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const defaultDate = sessionStorage.getItem("DEFAULT_DAY_OFFICE_DATE");
    const defaultStartTime = sessionStorage.getItem(
      "DEFAULT_DAY_OFFICE_START_TIME"
    );
    const defaultDuration = sessionStorage.getItem(
      "DEFAULT_DAY_OFFICE_DURATION"
    );
    const defaultCapacity = sessionStorage.getItem(
      "DEFAULT_DAY_OFFICE_CAPACITY"
    );
    const defaultAmenity = sessionStorage.getItem(
      "DEFAULT_DAY_OFFICE_AMENITIES"
    );
    const defaultSort = sessionStorage.getItem("DEFAULT_DAY_OFFICE_SORTBY");

    const defaultLocation = sessionStorage.getItem(
      "DEFAULT_DAY_OFFICE_LOCATION"
    );

    const defaultFilters = {
      ...dayOfficeFilterData,
      defaultDuration: defaultDuration ?? dayOfficeFilterData?.defaultDuration,
      capacity_select: defaultCapacity
        ? Number(defaultCapacity)
        : dayOfficeFilterData?.capacity_select,
      sort_by: defaultSort ? Number(defaultSort) : dayOfficeFilterData?.sort_by,
      amenities: defaultAmenity
        ? defaultAmenity.split(",").map(Number)
        : dayOfficeFilterData?.amenities,
      lat: defaultLocation ? JSON.parse(defaultLocation).lat : null,
      lng: defaultLocation ? JSON.parse(defaultLocation).lng : null,
      defaultDate: defaultDate ?? dayOfficeFilterData?.defaultDate,
      defaultStartTime:
        defaultStartTime ?? dayOfficeFilterData?.defaultStartTime,
    };

    dispatch(listDayOfficeFilterAction(defaultFilters));

    setPopulateFromSessionStorage(true);
  }, [populateFromSessionStorage]);

  // Only the fields below are part of the actual request. Keying the fetch on
  // them (instead of the whole filters object) collapses the burst of partial
  // filter updates fired during mount into a single request, and skips refetches
  // for UI-only fields such as defaultStartTime / isSetDefaultValue.
  const dayOfficeQueryKey = JSON.stringify({
    city_code: dayOfficeFilterData?.city_code,
    start_time: dayOfficeFilterData?.start_time,
    end_time: dayOfficeFilterData?.end_time,
    sort_by: dayOfficeFilterData?.sort_by,
    capacity_select: dayOfficeFilterData?.capacity_select,
    amenities: dayOfficeFilterData?.amenities,
    lat: dayOfficeFilterData?.lat,
    lng: dayOfficeFilterData?.lng,
  });

  useEffect(() => {
    if (!populateFromSessionStorage) return;
    // Wait until the booking time window has been computed. Fetching with a null
    // start/end time produces a wasteful, time-less query that is immediately
    // superseded once the default time is set.
    if (
      !dayOfficeFilterData?.city_code ||
      !dayOfficeFilterData?.start_time ||
      !dayOfficeFilterData?.end_time
    ) {
      return;
    }

    (async () => {
      try {
        setLoading(true);
        await dispatch<any>(getDayOfficeListThunk(dayOfficeFilterData));
        dispatch<any>(dayOfficeMarketCountThunk(dayOfficeFilterData));
      } finally {
        setLoading(false);
      }
    })();
  }, [dayOfficeQueryKey, populateFromSessionStorage]);

  return (
    <>
      <FilterDayOffice
        populateFromSessionStorage={populateFromSessionStorage}
      />
      <SizeBox height="16px" />
      <DayOfficeList loading={loading} />
      {modalConfirmBooking.open && <ModalConfirmBookingDayOffice />}
      <ModalBookingSubmittedDayOffice />
    </>
  );
};

export default DayOffice;
